// ============================================================================
// CASE NUMBER FORMAT HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { caseNumberFormatService } from '../../api/services/caseNumberFormatService';
import type {
  CaseNumberFormat,
  CaseNumberFormatListItem,
  CreateCaseNumberFormatRequest,
  UpdateCaseNumberFormatRequest,
  CaseNumberFormatSearchRequest,
} from '../../api/types/caseNumberFormat.types';
import type { ID } from '../../api/types';

export const useCaseNumberFormats = (params?: CaseNumberFormatSearchRequest) => {
  const [data, setData] = useState<CaseNumberFormatListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1, pageSize: 20, totalPages: 0, totalItems: 0,
    hasNextPage: false, hasPreviousPage: false,
  });

  const paramsKey = JSON.stringify(params);

  const fetchItems = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const response = await caseNumberFormatService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  return { data, loading, error, pagination, refetch: fetchItems };
};

export const useCaseNumberFormat = (id: ID | undefined) => {
  const [data, setData] = useState<CaseNumberFormat | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchItem = useCallback(async () => {
    if (!id) return;
    setLoading(true); setError(null);
    try {
      const response = await caseNumberFormatService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchItem(); }, [fetchItem]);

  return { data, loading, error, refetch: fetchItem };
};

export const useCreateCaseNumberFormat = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createCaseNumberFormat = async (
    data: CreateCaseNumberFormatRequest,
    options?: { onSuccess?: (item: CaseNumberFormat) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      const response = await caseNumberFormatService.create(data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { createCaseNumberFormat, loading, error };
};

export const useUpdateCaseNumberFormat = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateCaseNumberFormat = async (
    id: ID, data: UpdateCaseNumberFormatRequest,
    options?: { onSuccess?: (item: CaseNumberFormat) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      const response = await caseNumberFormatService.update(id, data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { updateCaseNumberFormat, loading, error };
};

export const useDeleteCaseNumberFormat = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteCaseNumberFormat = async (
    id: ID,
    options?: { onSuccess?: () => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      await caseNumberFormatService.delete(id);
      options?.onSuccess?.();
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { deleteCaseNumberFormat, loading, error };
};
